import { useMemo } from 'react';
import { GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { Box, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import z from 'zod';
import { MajorSchema, MajorYearStatsSchema } from '../../../services/majorApi';
import { useAppSelector } from '../../../store/hooks';
import { MOBILE_CELL_STYLES, MOBILE_GUIDE } from '../../../utils/responsive';

type Major = z.infer<typeof MajorSchema>;

interface UseMajorsColumnsProps {
	majorStats?: { stats: z.infer<typeof MajorYearStatsSchema>[] };
	isAdmin: boolean;
	isMobile?: boolean;
	onEdit: (major: Major) => () => void;
	onDelete: (major: Major) => () => void;
}

export function useMajorsColumns({ isAdmin, majorStats, isMobile = false, onEdit, onDelete }: UseMajorsColumnsProps) {
	const years = useAppSelector((state) => state.filters.years);

	return useMemo<GridColDef[]>(
		() => {
			// Mobile: condensed single column with code, name, faculty, and latest year
			if (isMobile) {
				const sortedYears = [...years].sort((a: number, b: number) => a - b);
				const latestYear = sortedYears[sortedYears.length - 1];
				const majorYearDataMap = new Map<string, Record<string, number>>();

				if (majorStats?.stats) {
					majorStats.stats.forEach((stat) => {
						majorYearDataMap.set(stat.maNganh, stat.yearData);
					});
				}

				return [
					{
						field: 'tenNganh',
						headerName: 'Ngành',
						minWidth: MOBILE_CELL_STYLES.MIN_WIDTH,
						flex: 1,
						sortable: true,
						filterable: false,
						renderCell: ({ row }) => {
							const yearData = majorYearDataMap.get(row.maNganh);
							const latestCount = latestYear && yearData ? yearData[latestYear.toString()] || 0 : 0;
							return (
								<Box
									sx={{
										width: '100%',
										height: '100%',
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'flex-start',
										justifyContent: 'center',
										padding: MOBILE_CELL_STYLES.PADDING,
										whiteSpace: 'normal',
										wordWrap: 'break-word',
										lineHeight: MOBILE_CELL_STYLES.LINE_HEIGHT,
										gap: MOBILE_CELL_STYLES.GAP,
									}}
								>
									<Typography variant="caption" color="text.secondary">
										{row.maNganh || 'N/A'}
									</Typography>
									<Typography variant="body2" fontWeight="bold">
										{row.tenNganh || 'N/A'}
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{row.tenKhoa || 'N/A'}
									</Typography>
									{latestYear && (
										<Typography variant="caption" color="text.secondary">
											{latestYear}: {latestCount} sinh viên
										</Typography>
									)}
									<Typography variant="caption" color={MOBILE_GUIDE.COLOR} fontWeight={MOBILE_GUIDE.FONT_WEIGHT}>
										{MOBILE_GUIDE.TEXT}
									</Typography>
								</Box>
							);
						},
					},
				];
			}

			const baseColumns: GridColDef[] = [
				{
					field: 'stt',
					headerName: 'STT',
					width: 60,
					headerAlign: 'center',
					align: 'center',
					sortable: false,
					filterable: false,
					renderCell: ({ api, id }) => {
						const currentPage = api.state.pagination.paginationModel.page;
						const pageSize = api.state.pagination.paginationModel.pageSize;
						const allRows = api.getRowModels();
						const rowIndex = Array.from(allRows.keys()).indexOf(id as string);
						const stt = currentPage * pageSize + rowIndex + 1;
						return stt;
					},
				},
				{
					field: 'maNganh',
					headerName: 'Mã Ngành',
					width: 150,
					headerAlign: 'center',
					align: 'center',
					sortable: true,
					filterable: false
				},
				{
					field: 'tenNganh',
					headerName: 'Tên Ngành',
					width: 300,
					headerAlign: 'center',
					sortable: true,
					filterable: false,
					renderCell: ({ value }) => (
						<Box
							sx={{
								width: '100%',
								height: '100%',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'flex-start',
								padding: '8px',
								whiteSpace: 'normal',
								wordWrap: 'break-word',
								lineHeight: 1.2,
							}}
						>
							{value || 'N/A'}
						</Box>
					),
				},
				{
					field: 'tenKhoa',
					headerName: 'Khoa',
					width: 200,
					headerAlign: 'center',
					sortable: true,
					filterable: false,
					renderCell: ({ value }) => (
						<Box
							sx={{
								width: '100%',
								height: '100%',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'flex-start',
								padding: '8px',
								whiteSpace: 'normal',
								wordWrap: 'break-word',
								lineHeight: 1.2,
							}}
						>
							{value || 'N/A'}
						</Box>
					),
				},
			];

			const yearColumns: GridColDef[] = [];
			if (years.length > 0 && majorStats?.stats) {
				const sortedYears = [...years].sort((a: number, b: number) => a - b);

				const majorYearDataMap = new Map<string, Record<string, number>>();
				const majorQuotaDataMap = new Map<string, Record<string, number>>();

				majorStats.stats.forEach((stat) => {
					majorYearDataMap.set(stat.maNganh, stat.yearData);
					if (stat.quotaData) {
						majorQuotaDataMap.set(stat.maNganh, stat.quotaData);
					}
				});

				sortedYears.slice(-5).forEach((year: number) => {
					yearColumns.push({
						field: year.toString(),
						headerName: year.toString(),
						width: 80,
						type: 'number',
						headerAlign: 'center',
						align: 'center',
						sortable: true,
						filterable: false,
						renderCell: ({ row }: { row: Major }) => {
							const yearData = majorYearDataMap.get(row.maNganh);
							const count = yearData?.[year.toString()] || 0;
							return count;
						},
					});

					yearColumns.push({
						field: `chiTieu_${year}`,
						headerName: `Chỉ tiêu ${year}`,
						// render header with line break
						renderHeader: () => (
							<Box
								sx={{
									fontWeight: 'bold',
									width: '100%',
									height: '100%',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									padding: '8px',
									whiteSpace: 'normal',
									wordWrap: 'break-word',
									lineHeight: 1.2,
									textAlign: 'center',
								}}
							>
								{`Chỉ tiêu ${year}`}
							</Box>
						),
						width: 100,
						type: 'number',
						headerAlign: 'center',
						align: 'center',
						sortable: false,
						filterable: false,
						renderCell: ({ row }: { row: Major }) => {
							const quotaData = majorQuotaDataMap.get(row.maNganh);
							const quota = quotaData?.[year.toString()] || 0;
							return quota;
						},
					});
				});
			}

			const actionColumn = isAdmin ? [{
				field: 'actions' as const,
				type: 'actions' as const,
				headerName: 'Thao tác',
				width: 120,
				headerAlign: 'center' as const,
				getActions: ({ row }: { row: Major }) => [
					<GridActionsCellItem
						key="edit-item"
						icon={<EditIcon />}
						label="Chỉnh sửa"
						onClick={onEdit(row)}
					/>,
					<GridActionsCellItem
						key="delete-item"
						icon={<DeleteIcon />}
						label="Xóa"
						onClick={onDelete(row)}
					/>,
				],
			}] : [];

			return [...baseColumns, ...yearColumns, ...actionColumn];
		},
		[isAdmin, years, majorStats, isMobile, onEdit, onDelete],
	);
}